import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {CloseIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Popover} from "@pg-design/notifications";
import {Text} from "@pg-design/text-module";
import {FormikForm, handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {castRtkErrorForFormik} from "@pg-mono/react-hook-forms-utils";
import {getPhoneDataFromString} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {resetRecommendedOffers} from "../../../offer/detail/actions/fetch_recommended_offers";
import {resetRecommendedProperties} from "../../../property/actions/fetch_recommended_properties";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useRegionEstimatedPrice} from "../../../region/hooks/use_region_estimated_price";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {applicationFormValidationSchema} from "../../constants/form/application_form_fields";
import {
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS,
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
} from "../../constants/multi_lead/application_multi_lead_max_distance";
import {WARSAW_REGION_ID} from "../../constants/multi_lead/application_multi_lead_region_ids";
import {useMultiLeadData} from "../../hooks/multi_lead/use_application_multi_lead_data";
import {useMultiLeadFormSend} from "../../hooks/multi_lead/use_application_multi_lead_form_send";
import {useMultiLeadRecommendations} from "../../hooks/multi_lead/use_application_multi_lead_recommendations";
import {useRecommendationsSelection} from "../../hooks/multi_lead/use_application_multi_lead_recommendations_selection";
import {useMultiLeadReport} from "../../hooks/multi_lead/use_multi_lead_report";
import {RecommendedOfferApplicationsType} from "../../hooks/multi_lead/use_track_recommended_offers_application";
import {useApplicationPropertyPriceEstimatedByArea} from "../../hooks/use_application_property_price_estimated_by_area";
import {hitMultileadSendButton} from "../../tracking/lead/hit_multilead_send_button";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {ApplicationSourceSection} from "../../utils/form/application_form_source";
import {ApplicationMultiLeadOfferRevealedPriceBox} from "./ApplicationMultiLeadOfferRevealedPriceBox";
import {ApplicationMultiLeadSimpleRecommendationsList} from "./ApplicationMultiLeadSimpleRecommendationsList";
import {ApplicationMultiLeadUserDataFormFields} from "./ApplicationMultiLeadUserDataFormFields";

// Constant values
const MAX_SHOWN_CURLED = 3;

export interface IMultiLeadSimpleRecommendationsModalProps {
    sourceSection: ApplicationSourceSection;
    onModalClose: () => void;
    onSuccessfulSubmit?: (applications: ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[]) => void;
    recommendationType: ApplicationMultiLeadRecommendationType;
    originalApplicationFormValues: IApplicationFormValues;
    recommendationsWithPriceFilter: boolean;
    customSourceSection?: string;
    originalApplicationResponse: IApplicationData | null;
}

const calcMaxRecommendations = (recommendations: IRecommendedProperty[], originApplicationRegionCityID: number | undefined) => {
    const maxRecommendationDistance =
        originApplicationRegionCityID && originApplicationRegionCityID === WARSAW_REGION_ID
            ? MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
            : MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS;

    const baseRecommendationsGroupAmount = recommendations.filter((recommendation) => recommendation.distance < maxRecommendationDistance).length;

    return baseRecommendationsGroupAmount > 0 && baseRecommendationsGroupAmount < MAX_SHOWN_CURLED ? baseRecommendationsGroupAmount : MAX_SHOWN_CURLED;
};

export const ApplicationMultiLeadSimpleRecommendationsModal = (props: IMultiLeadSimpleRecommendationsModalProps) => {
    const {onModalClose, originalApplicationFormValues, originalApplicationResponse, recommendationType, onSuccessfulSubmit} = props;

    const dispatch = useDispatch();
    const {viewType, originalApplicationRegionCityID, originalApplicationGeoPoint, originalApplicationCountry, applicationData} = useMultiLeadData();
    const {modalOpen, checkboxChange, mapPinClick} = useMultiLeadReport();
    const {recommendedOffers, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {regionEstimatedPriceSummary, requestState: regionEstimatedPriceSummaryRequestState} = useRegionEstimatedPrice({
        region: applicationData?.offer?.detail?.region
    });
    const {estimatedPriceInfoText} = useApplicationPropertyPriceEstimatedByArea({
        regionEstimatedPriceSummary,
        price: originalApplicationResponse?.property_price,
        isPriceRevealed: !!originalApplicationResponse?.property_price,
        area: applicationData?.property?.detail?.area,
        country: originalApplicationCountry
    });
    const {data: userProfile} = useGetUserProfileQuery();
    const [showPropertiesList, setShowPropertiesList] = useState(true);
    const [isMultiLeadSubmitting, setIsMultiLeadSubmitting] = useState(false);
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);
    // TODO: Bring back if we'll need to set some defaults again - CU-8695y5kux
    const maxRecommendationsShownOnCurl = calcMaxRecommendations(recommendedProperties, originalApplicationRegionCityID);

    const {selectedRecommendationsCount, selectedRecommendations, toggleRecommendation} = useRecommendationsSelection({
        recommendedOffers,
        recommendedProperties,
        maxRecommendationsShown: maxRecommendationsShownOnCurl,
        recommendationType
    });

    const isSendApplicationDisabled = selectedRecommendationsCount === 0;

    useEffect(() => {
        recommendedOffers.slice(3).map((el) => toggleRecommendation(el.id));
        return () => {
            // Clear recommended properties, offers and form values - we don't need this data anymore
            dispatch(resetRecommendedProperties());
            dispatch(resetRecommendedOffers());
        };
    }, []);

    useEffect(() => {
        modalOpen();
    }, []);

    const {sendMultiLeadForm} = useMultiLeadFormSend({...props, castRtkError: castRtkErrorForFormik});
    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const onSubmit: IFormikSubmitFn<Omit<IApplicationFormValues, "marketing_sms"> & Partial<Pick<IApplicationFormValues, "marketing_sms">>> = async (
        formValues,
        formikHelpers
    ) => {
        if (formValues?.email && !(await validateEmailOnce(formValues?.email))) {
            return;
        }

        setIsMultiLeadSubmitting(true);

        handleFormikPost(
            sendMultiLeadForm({
                selectedRecommendations,
                formValues: {
                    ...formValues,
                    marketing_sms: formValues.marketing_sms || false
                }
            }),
            formikHelpers,
            {
                onSuccess: (sentRecommendedLeads) => {
                    onSuccessfulSubmit && onSuccessfulSubmit(sentRecommendedLeads);
                },
                onFieldError: () => setIsMultiLeadSubmitting(false),
                onNonFieldError: () => setIsMultiLeadSubmitting(false)
            }
        );
    };

    const handleChange = (viewType: ViewType, recommendationId: number) => {
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        // GTM & Algolytics events
        checkboxChange(recommendationType, viewType, isCheckboxSelected, recommendationId);
    };

    const handlePinClick = (viewType: ViewType | null, recommendationId: number) => {
        if (!(recommendationType in ApplicationMultiLeadRecommendationType)) {
            throw new Error("Unknown recommendation type");
        }

        const isCheckboxSelected = toggleRecommendation(recommendationId);
        mapPinClick(viewType, recommendationId, isCheckboxSelected);
    };

    const onSendClick = () => {
        hitMultileadSendButton();
    };

    const isSingleLeadSent = Boolean(originalApplicationResponse);
    const isLeadPropertyAvailable = Boolean(applicationData?.property?.detail?.id);
    const isFormFilled = !!originalApplicationFormValues.email && !!originalApplicationFormValues.name && !!originalApplicationFormValues.phone.number;
    const initialValues = isFormFilled
        ? originalApplicationFormValues
        : {
              ...props.originalApplicationFormValues,
              email: userProfile?.email || "",
              name: userProfile?.name || "",
              phone: getPhoneDataFromString(userProfile?.phone || ""),
              marketing_email: typeof userProfile?.email_marketing_consent === "boolean" ? userProfile?.email_marketing_consent : true,
              marketing_sms: typeof userProfile?.phone_marketing_consent === "boolean" ? userProfile?.phone_marketing_consent : undefined
          };

    return (
        <div className={holder}>
            <FormikForm onSubmit={onSubmit} initialValues={initialValues} validationSchema={applicationFormValidationSchema} validateOnMount>
                {(formikProps) => {
                    return (
                        <div className={innerFormStyle}>
                            <div className={headerSectionStyle}>
                                <Text variant="headline_6" className={css({mr: 4, mb: 1})}>
                                    Zapytaj o podobne oferty w najbliższej okolicy.
                                </Text>

                                <div className={topPanelWrapper}>
                                    <Link underline onClick={() => setShowPropertiesList((prevState) => !prevState)}>
                                        {showPropertiesList ? "Zmień dane zapytania" : "Wybierz dodatkowe oferty"}
                                    </Link>
                                </div>
                            </div>
                            <div className={listSectionStyle}>
                                {showPropertiesList ? (
                                    <ApplicationMultiLeadSimpleRecommendationsList
                                        recommendationType={recommendationType}
                                        recommended={{
                                            properties: recommendedProperties,
                                            offers: recommendedOffers
                                        }}
                                        mainPropertyId={applicationData?.property?.detail?.id}
                                        allowToSelectMainProperty={false}
                                        originalApplicationGeoPoint={originalApplicationGeoPoint}
                                        originalApplicationCountry={originalApplicationCountry}
                                        originalApplicationEstimatedPrice={
                                            !applicationData?.property?.detail?.id && hasVerifiedPhone ? null : estimatedPriceInfoText
                                        }
                                        viewType={viewType}
                                        selected={selectedRecommendations}
                                        handleChange={handleChange}
                                        handlePinClick={handlePinClick}
                                        listTopSlot={
                                            isSingleLeadSent && isLeadPropertyAvailable ? (
                                                <ApplicationMultiLeadOfferRevealedPriceBox
                                                    offerType={applicationData?.offer?.detail?.type || 0}
                                                    offerAddress={applicationData?.offer?.detail?.address || ""}
                                                    offerMainImg={applicationData?.offer?.detail?.main_image.m_img_375x211 || ""}
                                                    rooms={applicationData?.property?.detail?.rooms}
                                                    area={applicationData?.property?.detail?.area}
                                                    estimatedPriceInfoText={hasVerifiedPhone ? estimatedPriceInfoText : null}
                                                    regionEstimatedPriceRequestState={regionEstimatedPriceSummaryRequestState}
                                                    className={css({mr: 0, mb: 2})}
                                                />
                                            ) : null
                                        }
                                    />
                                ) : (
                                    <div className={formFieldsHolder}>
                                        <ApplicationMultiLeadUserDataFormFields emailNotificationProps={emailNotificationProps} />
                                    </div>
                                )}
                            </div>

                            <div className={revealPriceBottomPanelStyle}>
                                <div className={revealPriceBottomPanelLeftColumnStyle}>
                                    <Popover
                                        arrow
                                        isActive={isSendApplicationDisabled}
                                        popoverPlace="top"
                                        body="Zaznacz propozycje powyżej aby wysłać zapytanie"
                                        toggleHoverHandlers
                                        toggleOnClickOutside
                                        toggleClickHandler
                                    >
                                        <>
                                            {!formikProps.isValid && showPropertiesList && (
                                                <Text variant="info_txt_3" as="p" className={css({color: "var(--colors-danger)", textAlign: "center"})}>
                                                    Nie można wysłać zapytań. Sprawdź poprawność danych{" "}
                                                    <div
                                                        onClick={() => setShowPropertiesList(false)}
                                                        className={css({textDecoration: "underline", display: "inline-block", cursor: "pointer"})}
                                                    >
                                                        klikając tu.
                                                    </div>
                                                </Text>
                                            )}
                                            <FormikForm.Button
                                                variant="filled_primary"
                                                disabled={isSendApplicationDisabled || isMultiLeadSubmitting || !formikProps.isValid}
                                                isLoading={isMultiLeadSubmitting}
                                                onClick={onSendClick}
                                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                                className={submitButtonStyle}
                                            >
                                                Zapytaj o oferty
                                            </FormikForm.Button>
                                        </>
                                    </Popover>
                                </div>
                            </div>

                            <div className={closeHolder} onClick={onModalClose}>
                                <CloseIcon size="2.4" />
                            </div>
                        </div>
                    );
                }}
            </FormikForm>
        </div>
    );
};

const holder = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",

    md: {
        height: "100%",
        minWidth: "950px"
    }
});

const innerFormStyle = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
});

const headerSectionStyle = css({
    pt: 1.5,
    px: 1.5,

    md: {
        px: 3,
        pt: 2
    }
});

const listSectionStyle = css({
    display: "flex",
    overflowY: "hidden",
    flexDirection: "column",

    md: {
        flex: "0 0 450px",
        paddingX: 3
    }
});

const topPanelWrapper = css({
    pb: 3,
    display: "flex",
    alignItems: "normal",
    justifyContent: "space-between"
});

const closeHolder = css({
    cursor: "pointer",
    position: "absolute",
    top: "2rem",
    right: "2.4rem"
});

const submitButtonStyle = css({
    w: "100%",

    md: {
        minWidth: "20rem"
    }
});

const revealPriceBottomPanelStyle = css({
    py: 2,
    px: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    width: "100%",
    position: "sticky",
    bottom: 0,
    backgroundColor: "var(--colors-white)",

    sm: {
        px: 3
    }
});

const revealPriceBottomPanelLeftColumnStyle = css({
    maxWidth: "460px",
    width: "100%"
});

const formFieldsHolder = css({
    py: 1.5,
    px: 3,

    md: {
        py: 2
    }
});
import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {CloseIcon} from "@pg-design/icons-module";
import {Link} from "@pg-design/link-module";
import {Popover} from "@pg-design/notifications";
import {Text} from "@pg-design/text-module";
import {FormikForm, handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {castRtkErrorForFormik} from "@pg-mono/react-hook-forms-utils";
import {getPhoneDataFromString} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {resetRecommendedOffers} from "../../../offer/detail/actions/fetch_recommended_offers";
import {resetRecommendedProperties} from "../../../property/actions/fetch_recommended_properties";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {useRegionEstimatedPrice} from "../../../region/hooks/use_region_estimated_price";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useEmailVerification} from "../../../user/hooks/use_email_verification";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {applicationFormValidationSchema} from "../../constants/form/application_form_fields";
import {
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS,
    MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
} from "../../constants/multi_lead/application_multi_lead_max_distance";
import {WARSAW_REGION_ID} from "../../constants/multi_lead/application_multi_lead_region_ids";
import {useMultiLeadData} from "../../hooks/multi_lead/use_application_multi_lead_data";
import {useMultiLeadFormSend} from "../../hooks/multi_lead/use_application_multi_lead_form_send";
import {useMultiLeadRecommendations} from "../../hooks/multi_lead/use_application_multi_lead_recommendations";
import {useRecommendationsSelection} from "../../hooks/multi_lead/use_application_multi_lead_recommendations_selection";
import {useMultiLeadReport} from "../../hooks/multi_lead/use_multi_lead_report";
import {RecommendedOfferApplicationsType} from "../../hooks/multi_lead/use_track_recommended_offers_application";
import {useApplicationPropertyPriceEstimatedByArea} from "../../hooks/use_application_property_price_estimated_by_area";
import {hitMultileadSendButton} from "../../tracking/lead/hit_multilead_send_button";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../types/IApplicationData";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {ApplicationSourceSection} from "../../utils/form/application_form_source";
import {ApplicationMultiLeadOfferRevealedPriceBox} from "./ApplicationMultiLeadOfferRevealedPriceBox";
import {ApplicationMultiLeadSimpleRecommendationsList} from "./ApplicationMultiLeadSimpleRecommendationsList";
import {ApplicationMultiLeadUserDataFormFields} from "./ApplicationMultiLeadUserDataFormFields";

// Constant values
const MAX_SHOWN_CURLED = 3;

export interface IMultiLeadSimpleRecommendationsModalProps {
    sourceSection: ApplicationSourceSection;
    onModalClose: () => void;
    onSuccessfulSubmit?: (applications: ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[]) => void;
    recommendationType: ApplicationMultiLeadRecommendationType;
    originalApplicationFormValues: IApplicationFormValues;
    recommendationsWithPriceFilter: boolean;
    customSourceSection?: string;
    originalApplicationResponse: IApplicationData | null;
}

const calcMaxRecommendations = (recommendations: IRecommendedProperty[], originApplicationRegionCityID: number | undefined) => {
    const maxRecommendationDistance =
        originApplicationRegionCityID && originApplicationRegionCityID === WARSAW_REGION_ID
            ? MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_WARSAW
            : MAX_DISTANCE_FROM_ORIGINAL_OFFER_KM_ALL_REGIONS;

    const baseRecommendationsGroupAmount = recommendations.filter((recommendation) => recommendation.distance < maxRecommendationDistance).length;

    return baseRecommendationsGroupAmount > 0 && baseRecommendationsGroupAmount < MAX_SHOWN_CURLED ? baseRecommendationsGroupAmount : MAX_SHOWN_CURLED;
};

export const ApplicationMultiLeadSimpleRecommendationsModal = (props: IMultiLeadSimpleRecommendationsModalProps) => {
    const {onModalClose, originalApplicationFormValues, originalApplicationResponse, recommendationType, onSuccessfulSubmit} = props;

    const dispatch = useDispatch();
    const {viewType, originalApplicationRegionCityID, originalApplicationGeoPoint, originalApplicationCountry, applicationData} = useMultiLeadData();
    const {modalOpen, checkboxChange, mapPinClick} = useMultiLeadReport();
    const {recommendedOffers, recommendedProperties} = useMultiLeadRecommendations({
        propertyId: applicationData?.property?.detail?.id,
        offerId: applicationData?.offer?.detail?.id
    });
    const {regionEstimatedPriceSummary, requestState: regionEstimatedPriceSummaryRequestState} = useRegionEstimatedPrice({
        region: applicationData?.offer?.detail?.region
    });
    const {estimatedPriceInfoText} = useApplicationPropertyPriceEstimatedByArea({
        regionEstimatedPriceSummary,
        price: originalApplicationResponse?.property_price,
        isPriceRevealed: !!originalApplicationResponse?.property_price,
        area: applicationData?.property?.detail?.area,
        country: originalApplicationCountry
    });
    const {data: userProfile} = useGetUserProfileQuery();
    const [showPropertiesList, setShowPropertiesList] = useState(true);
    const [isMultiLeadSubmitting, setIsMultiLeadSubmitting] = useState(false);
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);
    // TODO: Bring back if we'll need to set some defaults again - CU-8695y5kux
    const maxRecommendationsShownOnCurl = calcMaxRecommendations(recommendedProperties, originalApplicationRegionCityID);

    const {selectedRecommendationsCount, selectedRecommendations, toggleRecommendation} = useRecommendationsSelection({
        recommendedOffers,
        recommendedProperties,
        maxRecommendationsShown: maxRecommendationsShownOnCurl,
        recommendationType
    });

    const isSendApplicationDisabled = selectedRecommendationsCount === 0;

    useEffect(() => {
        recommendedOffers.slice(3).map((el) => toggleRecommendation(el.id));
        return () => {
            // Clear recommended properties, offers and form values - we don't need this data anymore
            dispatch(resetRecommendedProperties());
            dispatch(resetRecommendedOffers());
        };
    }, []);

    useEffect(() => {
        modalOpen();
    }, []);

    const {sendMultiLeadForm} = useMultiLeadFormSend({...props, castRtkError: castRtkErrorForFormik});
    const {props: emailNotificationProps, validateEmailOnce} = useEmailVerification();

    const onSubmit: IFormikSubmitFn<Omit<IApplicationFormValues, "marketing_sms"> & Partial<Pick<IApplicationFormValues, "marketing_sms">>> = async (
        formValues,
        formikHelpers
    ) => {
        if (formValues?.email && !(await validateEmailOnce(formValues?.email))) {
            return;
        }

        setIsMultiLeadSubmitting(true);

        handleFormikPost(
            sendMultiLeadForm({
                selectedRecommendations,
                formValues: {
                    ...formValues,
                    marketing_sms: formValues.marketing_sms || false
                }
            }),
            formikHelpers,
            {
                onSuccess: (sentRecommendedLeads) => {
                    onSuccessfulSubmit && onSuccessfulSubmit(sentRecommendedLeads);
                },
                onFieldError: () => setIsMultiLeadSubmitting(false),
                onNonFieldError: () => setIsMultiLeadSubmitting(false)
            }
        );
    };

    const handleChange = (viewType: ViewType, recommendationId: number) => {
        const isCheckboxSelected = toggleRecommendation(recommendationId);
        // GTM & Algolytics events
        checkboxChange(recommendationType, viewType, isCheckboxSelected, recommendationId);
    };

    const handlePinClick = (viewType: ViewType | null, recommendationId: number) => {
        if (!(recommendationType in ApplicationMultiLeadRecommendationType)) {
            throw new Error("Unknown recommendation type");
        }

        const isCheckboxSelected = toggleRecommendation(recommendationId);
        mapPinClick(viewType, recommendationId, isCheckboxSelected);
    };

    const onSendClick = () => {
        hitMultileadSendButton();
    };

    const isSingleLeadSent = Boolean(originalApplicationResponse);
    const isLeadPropertyAvailable = Boolean(applicationData?.property?.detail?.id);
    const isFormFilled = !!originalApplicationFormValues.email && !!originalApplicationFormValues.name && !!originalApplicationFormValues.phone.number;
    const initialValues = isFormFilled
        ? originalApplicationFormValues
        : {
              ...props.originalApplicationFormValues,
              email: userProfile?.email || "",
              name: userProfile?.name || "",
              phone: getPhoneDataFromString(userProfile?.phone || ""),
              marketing_email: typeof userProfile?.email_marketing_consent === "boolean" ? userProfile?.email_marketing_consent : true,
              marketing_sms: typeof userProfile?.phone_marketing_consent === "boolean" ? userProfile?.phone_marketing_consent : undefined
          };

    return (
        <div className={holder}>
            <FormikForm onSubmit={onSubmit} initialValues={initialValues} validationSchema={applicationFormValidationSchema} validateOnMount>
                {(formikProps) => {
                    return (
                        <div className={innerFormStyle}>
                            <div className={headerSectionStyle}>
                                <Text variant="headline_6" className={css({mr: 4, mb: 1})}>
                                    Zapytaj o podobne oferty w najbliższej okolicy.
                                </Text>

                                <div className={topPanelWrapper}>
                                    <Link underline onClick={() => setShowPropertiesList((prevState) => !prevState)}>
                                        {showPropertiesList ? "Zmień dane zapytania" : "Wybierz dodatkowe oferty"}
                                    </Link>
                                </div>
                            </div>
                            <div className={listSectionStyle}>
                                {showPropertiesList ? (
                                    <ApplicationMultiLeadSimpleRecommendationsList
                                        recommendationType={recommendationType}
                                        recommended={{
                                            properties: recommendedProperties,
                                            offers: recommendedOffers
                                        }}
                                        mainPropertyId={applicationData?.property?.detail?.id}
                                        allowToSelectMainProperty={false}
                                        originalApplicationGeoPoint={originalApplicationGeoPoint}
                                        originalApplicationCountry={originalApplicationCountry}
                                        originalApplicationEstimatedPrice={
                                            !applicationData?.property?.detail?.id && hasVerifiedPhone ? null : estimatedPriceInfoText
                                        }
                                        viewType={viewType}
                                        selected={selectedRecommendations}
                                        handleChange={handleChange}
                                        handlePinClick={handlePinClick}
                                        listTopSlot={
                                            isSingleLeadSent && isLeadPropertyAvailable ? (
                                                <ApplicationMultiLeadOfferRevealedPriceBox
                                                    offerType={applicationData?.offer?.detail?.type || 0}
                                                    offerAddress={applicationData?.offer?.detail?.address || ""}
                                                    offerMainImg={applicationData?.offer?.detail?.main_image.m_img_375x211 || ""}
                                                    rooms={applicationData?.property?.detail?.rooms}
                                                    area={applicationData?.property?.detail?.area}
                                                    estimatedPriceInfoText={hasVerifiedPhone ? estimatedPriceInfoText : null}
                                                    regionEstimatedPriceRequestState={regionEstimatedPriceSummaryRequestState}
                                                    className={css({mr: 0, mb: 2})}
                                                />
                                            ) : null
                                        }
                                    />
                                ) : (
                                    <div className={formFieldsHolder}>
                                        <ApplicationMultiLeadUserDataFormFields emailNotificationProps={emailNotificationProps} />
                                    </div>
                                )}
                            </div>

                            <div className={revealPriceBottomPanelStyle}>
                                <div className={revealPriceBottomPanelLeftColumnStyle}>
                                    <Popover
                                        arrow
                                        isActive={isSendApplicationDisabled}
                                        popoverPlace="top"
                                        body="Zaznacz propozycje powyżej aby wysłać zapytanie"
                                        toggleHoverHandlers
                                        toggleOnClickOutside
                                        toggleClickHandler
                                    >
                                        <>
                                            {!formikProps.isValid && showPropertiesList && (
                                                <Text variant="info_txt_3" as="p" className={css({color: "var(--colors-danger)", textAlign: "center"})}>
                                                    Nie można wysłać zapytań. Sprawdź poprawność danych{" "}
                                                    <div
                                                        onClick={() => setShowPropertiesList(false)}
                                                        className={css({textDecoration: "underline", display: "inline-block", cursor: "pointer"})}
                                                    >
                                                        klikając tu.
                                                    </div>
                                                </Text>
                                            )}
                                            <FormikForm.Button
                                                variant="filled_primary"
                                                disabled={isSendApplicationDisabled || isMultiLeadSubmitting || !formikProps.isValid}
                                                isLoading={isMultiLeadSubmitting}
                                                onClick={onSendClick}
                                                dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                                                className={submitButtonStyle}
                                            >
                                                Zapytaj o oferty
                                            </FormikForm.Button>
                                        </>
                                    </Popover>
                                </div>
                            </div>

                            <div className={closeHolder} onClick={onModalClose}>
                                <CloseIcon size="2.4" />
                            </div>
                        </div>
                    );
                }}
            </FormikForm>
        </div>
    );
};

const holder = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",

    md: {
        height: "100%",
        minWidth: "950px"
    }
});

const innerFormStyle = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
});

const headerSectionStyle = css({
    pt: 1.5,
    px: 1.5,

    md: {
        px: 3,
        pt: 2
    }
});

const listSectionStyle = css({
    display: "flex",
    overflowY: "hidden",
    flexDirection: "column",

    md: {
        flex: "0 0 450px",
        paddingX: 3
    }
});

const topPanelWrapper = css({
    pb: 3,
    display: "flex",
    alignItems: "normal",
    justifyContent: "space-between"
});

const closeHolder = css({
    cursor: "pointer",
    position: "absolute",
    top: "2rem",
    right: "2.4rem"
});

const submitButtonStyle = css({
    w: "100%",

    md: {
        minWidth: "20rem"
    }
});

const revealPriceBottomPanelStyle = css({
    py: 2,
    px: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    width: "100%",
    position: "sticky",
    bottom: 0,
    backgroundColor: "var(--colors-white)",

    sm: {
        px: 3
    }
});

const revealPriceBottomPanelLeftColumnStyle = css({
    maxWidth: "460px",
    width: "100%"
});

const formFieldsHolder = css({
    py: 1.5,
    px: 3,

    md: {
        py: 2
    }
});
